/**
 * Agent observability service for tracking agent search and profile view failures.
 *
 * Only failure events are tracked. Tenant ID is auto-injected by the event-tracker from hostname.
 */

import { trackEvent } from '../utils/event-tracker';
import {
  AGENT_SEARCH_FAILURE,
  AGENT_PROFILE_VIEW_FAILURE,
  AGENT_RECAPTCHA_TOKEN_FAILURE,
} from '../constants/agent-events';

/**
 * Search criteria parts; the service builds the search query string from these.
 */
export interface AgentSearchCriteria {
  name?: string;
  location?: string;
  isCommercial?: boolean;
}

/**
 * Parameters for tracking a failed agent search.
 */
export interface AgentSearchFailureParams {
  /** The error that caused the failure */
  error: unknown;
  /** Search criteria used; service builds the searchQuery string for the event */
  searchCriteria?: AgentSearchCriteria;
}

function buildSearchQueryFromCriteria(criteria?: AgentSearchCriteria): string | undefined {
  if (!criteria) return undefined;
  const parts: string[] = [];
  if (criteria.name?.length) parts.push(`name:${criteria.name}`);
  if (criteria.location?.length) parts.push(`location:${criteria.location}`);
  if (criteria.isCommercial) parts.push('type:commercial');
  return parts.length > 0 ? parts.join(', ') : undefined;
}

/**
 * Parameters for tracking a failed agent profile view.
 */
export interface AgentProfileViewFailureParams {
  /** The error that caused the failure */
  error: unknown;
  /** Agent ID that was attempted to be viewed */
  agentId: string;
}

/**
 * Parameters for tracking reCAPTCHA token generation failure.
 */
export interface AgentRecaptchaTokenFailureParams {
  /** The error that caused the failure (optional) */
  error?: unknown;
}

/**
 * Centralized error messages and error factory functions for reCAPTCHA token generation.
 */
export const RecaptchaErrors = {
  /**
   * Default error when reCAPTCHA token generation fails without a specific reason.
   */
  GENERATION_FAILED: 'reCAPTCHA token generation failed',
  
  /**
   * Error when token generation times out.
   * @param timeoutMs - The timeout value in milliseconds
   */
  timeout: (timeoutMs: number): string => `Token generation timeout after ${timeoutMs}ms`,
  
  /**
   * Error when reCAPTCHA key is not configured.
   */
  KEY_NOT_CONFIGURED: 'reCAPTCHA key not configured',
  
  /**
   * Error when the received token format is invalid.
   */
  INVALID_TOKEN_FORMAT: 'Invalid token format received',
  
  /**
   * Error when reCAPTCHA library fails to load after max retries.
   */
  LIBRARY_LOAD_FAILED: 'reCAPTCHA library failed to load after max retries',
} as const;

/**
 * Factory functions for creating reCAPTCHA-related Error objects.
 */
export const createRecaptchaError = {
  /**
   * Creates an error for reCAPTCHA token generation failure.
   */
  generationFailed: (): Error => new Error(RecaptchaErrors.GENERATION_FAILED),
  
  /**
   * Creates an error for token generation timeout.
   * @param timeoutMs - The timeout value in milliseconds
   */
  timeout: (timeoutMs: number): Error => new Error(RecaptchaErrors.timeout(timeoutMs)),
  
  /**
   * Creates an error for missing reCAPTCHA key configuration.
   */
  keyNotConfigured: (): Error => new Error(RecaptchaErrors.KEY_NOT_CONFIGURED),
  
  /**
   * Creates an error for invalid token format.
   */
  invalidTokenFormat: (): Error => new Error(RecaptchaErrors.INVALID_TOKEN_FORMAT),
  
  /**
   * Creates an error for library load failure.
   */
  libraryLoadFailed: (): Error => new Error(RecaptchaErrors.LIBRARY_LOAD_FAILED),
};

/**
 * Normalizes an error to a serializable format for observability tracking.
 * 
 * Converts Error objects to plain objects with their properties, and passes
 * other types through as-is (strings, objects, etc.).
 * 
 * @param error - The error to normalize
 * @returns A serializable representation of the error
 */
function normalizeError(error: unknown): unknown {
  if (!error) {
    return 'Unknown error';
  }
  
  // If it's an Error object, extract its properties
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
      ...(error.cause && { cause: error.cause }),
    };
  }
  
  // If it's already a string or plain object, pass it through
  return error;
}

export const AgentObservabilityService = {
  trackSearchFailure(params: AgentSearchFailureParams): void {
    const { error, searchCriteria } = params;
    const searchQuery = buildSearchQueryFromCriteria(searchCriteria);
    trackEvent(AGENT_SEARCH_FAILURE, {
      error: normalizeError(error),
      ...(searchQuery !== undefined && { searchQuery }),
    });
  },

  trackProfileViewFailure(params: AgentProfileViewFailureParams): void {
    const { error, agentId } = params;
    trackEvent(AGENT_PROFILE_VIEW_FAILURE, {
      error: normalizeError(error),
      agentId,
    });
  },

  trackRecaptchaTokenFailure(params: AgentRecaptchaTokenFailureParams): void {
    const { error } = params;
    trackEvent(AGENT_RECAPTCHA_TOKEN_FAILURE, {
      error: error ? normalizeError(error) : 'Token generation failed',
    });
  },
} as const;
